import React, { createContext, useContext, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SequenceStatusEnum, SequenceType } from "../../../types/Questionnaire";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/ConfigContext";
import {
  getApiSequences,
  getApiFullSequence,
  FullSequenceResponse,
} from "../../../services/questionnaire.service";
import { getUserScores } from "../../../services/sequence.service";

interface Score {
  id: number;
  userSequenceId: number;
  sequenceId: number;
  sequenceComponentId: number;
  questionnaireId: number;
  score: number;
  doneAt: string;
}

interface ResonanceDataContextValue {
  sequences: SequenceType[];
  scores: Score[];
  loading: boolean;
  error: Error | null;

  fetchSequences: () => Promise<SequenceType[] | undefined>;
  fetchScores: () => Promise<void>;

  fullSequenceByUserSequenceId: Record<number, FullSequenceResponse>;

  // ✅ UPDATED SIGNATURE
  prefetchFullSequencesForStudySequenceIds: (
    studySequenceIds: number[],
    seqs?: SequenceType[]
  ) => Promise<void>;

  updateScore: (
    id: number,
    userSequenceId: number,
    sequenceId: number,
    sequenceComponentId: number,
    questionnaireId: number,
    score: number
  ) => void;

  updateSequenceComponentDone: (userSequenceId: number, sequenceComponentId: number) => void;

  updateSequenceComponentProgress: (
    userSequenceId: number,
    sequenceComponentId: number,
    answeredCount: number
  ) => void;

  updateSequenceDone: (userSequenceId: number) => void;
}

const ResonanceDataContext = createContext<ResonanceDataContextValue>({
  sequences: [],
  scores: [],
  loading: false,
  error: null,

  fetchSequences: async () => undefined,
  fetchScores: async () => {},

  fullSequenceByUserSequenceId: {},
  prefetchFullSequencesForStudySequenceIds: async () => {},

  updateScore: () => {},
  updateSequenceComponentDone: () => {},
  updateSequenceComponentProgress: () => {},
  updateSequenceDone: () => {},
});

export const ResonanceDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { study } = useConfig();
  const {
    i18n: { language },
  } = useTranslation();

  const [sequences, setSequences] = useState<SequenceType[]>([]);
  const [scores, setScores] = useState<Score[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [fullSequenceByUserSequenceId, setFullSequenceByUserSequenceId] =
    useState<Record<number, FullSequenceResponse>>({});

  const fetchSequences = async (): Promise<SequenceType[] | undefined> => {
    if (!user || !study?.id) return;
    setLoading(true);
    setError(null);

    try {
      const response = await getApiSequences({
        studyId: study.id,
        userId: user.id,
        lang: language,
      });
      setSequences(response.data);
      return response.data;
    } catch (err: any) {
      console.error("Failed to fetch sequences", err);
      setError(err);
      return;
    } finally {
      setLoading(false);
    }
  };

  interface ScoresResponse {
    scores: Score[];
    success?: boolean;
  }

  const fetchScores = async () => {
    if (!user || !study?.id) return;
    try {
      const response = await getUserScores({
        studyId: study.id,
        userId: user.id,
      });

      const raw = response.data;

      let scoresArray: Score[] = [];
      if (Array.isArray(raw)) {
        scoresArray = raw as Score[];
      } else if (raw && Array.isArray((raw as ScoresResponse).scores)) {
        scoresArray = (raw as ScoresResponse).scores;
      }

      setScores(scoresArray);
    } catch (err: any) {
      console.error("Failed to fetch scores", err);
      setError(err);
      setScores([]);
    }
  };

  // ✅ Prefetch FullSequence for inline study sequence ids
  const prefetchFullSequencesForStudySequenceIds = async (
    studySequenceIds: number[],
    seqs?: SequenceType[]
  ) => {
    const source = seqs ?? sequences;
    if (!user || !study?.id) return;
    if (!studySequenceIds?.length) return;

    console.log("Starting prefetch for studySequenceIds:", studySequenceIds);

    const targets = source
      .filter((s) => studySequenceIds.includes(s.sequence_id))
      .map((s) => s.id); // ✅ USER sequence id

    const uniqueTargets = Array.from(new Set(targets)).filter(
      (userSequenceId) => !fullSequenceByUserSequenceId[userSequenceId]
    );

    console.log("Prefetch target userSequenceIds:", uniqueTargets);

    if (!uniqueTargets.length) return;

    const results = await Promise.allSettled(
      uniqueTargets.map((userSequenceId) => {
        console.log("Prefetching FULL sequence for userSequenceId:", userSequenceId);
        return getApiFullSequence({
          studyId: study.id,
          userId: user.id,
          sequenceId: userSequenceId,
          lang: language,
        }).then((r) => ({ userSequenceId, data: r.data }));
      })
    );

    setFullSequenceByUserSequenceId((prev) => {
      const next = { ...prev };
      for (const r of results) {
        if (r.status === "fulfilled") {
          next[r.value.userSequenceId] = r.value.data;
        }
      }
      console.log("Cache updated. Keys now:", Object.keys(next));
      return next;
    });
  };

  // --- existing updateScore / updateSequence* code unchanged ---
  const updateScore = (
    id: number,
    userSequenceId: number,
    sequenceId: number,
    sequenceComponentId: number,
    questionnaireId: number,
    score: number
  ) => {
    setScores((prev) => {
      const existingIndex = prev.findIndex(
        (s) =>
          s.userSequenceId === userSequenceId &&
          s.sequenceComponentId === sequenceComponentId &&
          s.questionnaireId === questionnaireId
      );

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          id,
          score,
          doneAt: new Date().toISOString(),
        };
        return updated;
      }

      return [
        ...prev,
        {
          id,
          userSequenceId,
          sequenceId,
          sequenceComponentId,
          questionnaireId,
          score,
          doneAt: new Date().toISOString(),
        },
      ];
    });
  };

  const updateSequenceComponentDone = (userSequenceId: number, sequenceComponentId: number) => {
    setSequences((prev) =>
      prev.map((seq) =>
        seq.id === userSequenceId
          ? {
              ...seq,
              components: seq.components.map((c) =>
                c.id === sequenceComponentId
                  ? { ...c, is_done: true, done_at: new Date().toISOString() }
                  : c
              ),
            }
          : seq
      )
    );
  };

  const updateSequenceComponentProgress = (
    userSequenceId: number,
    sequenceComponentId: number,
    answeredCount: number
  ) => {
    setSequences((prev) =>
      prev.map((seq) =>
        seq.id === userSequenceId
          ? {
              ...seq,
              components: seq.components.map((c) =>
                c.id === sequenceComponentId
                  ? { ...c, answered_questions_count: answeredCount }
                  : c
              ),
            }
          : seq
      )
    );
  };

  const updateSequenceDone = (userSequenceId: number) => {
    setSequences((prev) =>
      prev.map((seq) =>
        seq.id === userSequenceId ? { ...seq, status: SequenceStatusEnum.DONE } : seq
      )
    );
  };

  return (
    <ResonanceDataContext.Provider
      value={{
        sequences,
        scores,
        loading,
        error,
        fetchSequences,
        fetchScores,
        fullSequenceByUserSequenceId,
        prefetchFullSequencesForStudySequenceIds,
        updateScore,
        updateSequenceComponentDone,
        updateSequenceComponentProgress,
        updateSequenceDone,
      }}
    >
      {children}
    </ResonanceDataContext.Provider>
  );
};

export const useResonanceData = () => useContext(ResonanceDataContext);
